import { useState, useEffect } from "react";
import { format, addDays, addWeeks, nextSaturday, isSameDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Task, TaskPriority, TaskStatus } from "@/types/task-management";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  CalendarIcon, 
  X, 
  Calendar as CalendarIcon2,
  Sun,
  Clock,
  CalendarRange,
  XCircle
} from "lucide-react";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { TaskService } from "@/services/taskService";

interface TaskFormProps {
  task?: Task;
  initialValues?: Partial<Task>;
  onSubmit: (task: Partial<Task>) => void;
  onCancel?: () => void;
  extraButtons?: React.ReactNode;
  submitButtonLabel?: string;
}

// Schema de validação 
const taskSchema = z.object({
  title: z.string().min(1, "O título é obrigatório"),
  description: z.string().optional(),
  status: z.enum(["todo", "in_progress", "waiting", "completed", "cancelled"]),
  priority: z.enum(["low", "medium", "high", "urgent"]),
  due_date: z.date().optional().nullable(),
  added_to_my_day: z.boolean().optional().default(false),
  is_personal: z.boolean().optional().default(true),
  // Outros campos podem ser adicionados conforme necessário
});

export default function TaskForm({ 
  task, 
  initialValues,
  onSubmit,
  onCancel,
  extraButtons,
  submitButtonLabel
}: TaskFormProps) {
  const isEditing = !!task;
  const [labels, setLabels] = useState<any[]>([]);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);
  
  // Buscar labels disponíveis
  useEffect(() => {
    const fetchLabels = async () => {
      try {
        setIsLoadingLabels(true);
        console.log("Buscando etiquetas disponíveis...");
        const fetchedLabels = await TaskService.getTaskLabels();
        console.log("Etiquetas carregadas:", fetchedLabels);
        setLabels(fetchedLabels);
      } catch (error) {
        console.error("Erro ao buscar labels:", error);
      } finally {
        setIsLoadingLabels(false);
      }
    };
    
    fetchLabels();
  }, []);
  
  // Combinar valores do task com initialValues, priorizando initialValues
  const getInitialFormValues = () => {
    const baseValues = task || {};
    const combinedValues = {
      ...baseValues,
      ...(initialValues || {})
    };
    
    return {
      title: combinedValues.title || "",
      description: combinedValues.description || "",
      status: combinedValues.status || "todo",
      priority: combinedValues.priority || "medium",
      due_date: combinedValues.due_date ? new Date(combinedValues.due_date) : null,
      added_to_my_day: combinedValues.added_to_my_day || false,
      is_personal: combinedValues.is_personal !== false, // true por padrão
    };
  };
  
  // Configurar React Hook Form com Zod
  const form = useForm<z.infer<typeof taskSchema>>({
    resolver: zodResolver(taskSchema),
    defaultValues: getInitialFormValues(),
  });
  
  // Estado para gerenciar labels selecionados
  const [selectedLabels, setSelectedLabels] = useState<string[]>(
    task?.labels || initialValues?.labels || []
  );
  
  // Formatar data para exibição
  const formatDate = (date?: Date) => {
    if (!date) return "";
    return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };
  
  // Funções para obter datas predefinidas
  const getToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };
  
  const getTomorrow = () => {
    return addDays(getToday(), 1);
  };
  
  const getNextWeek = () => {
    return addWeeks(getToday(), 1);
  };
  
  const getNextWeekend = () => {
    return nextSaturday(getToday());
  };
  
  // Obter o dia da semana para exibição
  const getWeekday = (date: Date) => {
    const days = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
    return days[date.getDay() === 0 ? 6 : date.getDay() - 1];
  };
  
  // Obter mês abreviado
  const getMonthShort = (date: Date) => {
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return months[date.getMonth()];
  };
  
  // Manipulador do formulário
  const handleSubmit = (values: z.infer<typeof taskSchema>) => {
    onSubmit({
      ...values,
      due_date: values.due_date ? values.due_date.toISOString() : undefined,
      labels: selectedLabels,
      // Não incluir assigned_to para evitar problemas com chave estrangeira
      assigned_to: undefined,
      // Incluir id se estiver editando
      ...(isEditing ? { id: task.id } : {}),
    });
  };
  
  const removeLabel = (labelId: string) => {
    setSelectedLabels(prev => prev.filter(id => id !== labelId));
  };
  
  const addLabel = (labelId: string) => {
    if (!selectedLabels.includes(labelId)) {
      setSelectedLabels(prev => [...prev, labelId]);
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Título</FormLabel>
              <FormControl>
                <Input placeholder="Título da tarefa" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descrição</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Descrição da tarefa" 
                  className="resize-none min-h-[100px]" 
                  {...field} 
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="todo">A fazer</SelectItem>
                    <SelectItem value="in_progress">Em progresso</SelectItem>
                    <SelectItem value="waiting">Aguardando</SelectItem>
                    <SelectItem value="completed">Concluída</SelectItem>
                    <SelectItem value="cancelled">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Prioridade</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a prioridade" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="due_date"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Data de vencimento</FormLabel>
              
              {/* Botões de data rápida */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                <Button
                  type="button"
                  size="sm"
                  variant={field.value && isSameDay(field.value, getToday()) ? "default" : "outline"}
                  className="flex items-center justify-between px-3 h-9"
                  onClick={() => field.onChange(getToday())}
                >
                  <div className="flex items-center gap-2">
                    <CalendarIcon2 className="h-4 w-4 text-green-500" />
                    <span>Today</span>
                  </div>
                  <span className="text-muted-foreground text-xs">Sex</span>
                </Button>
                
                <Button
                  type="button"
                  size="sm"
                  variant={field.value && isSameDay(field.value, getTomorrow()) ? "default" : "outline"}
                  className="flex items-center justify-between px-3 h-9"
                  onClick={() => field.onChange(getTomorrow())}
                >
                  <div className="flex items-center gap-2">
                    <Sun className="h-4 w-4 text-amber-500" />
                    <span>Tomorrow</span>
                  </div>
                  <span className="text-muted-foreground text-xs">Sáb</span>
                </Button>
                
                <Button
                  type="button"
                  size="sm"
                  variant={field.value && isSameDay(field.value, getNextWeek()) ? "default" : "outline"}
                  className="flex items-center justify-between px-3 h-9"
                  onClick={() => field.onChange(getNextWeek())}
                >
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-purple-500" />
                    <span>Next week</span>
                  </div>
                  <span className="text-muted-foreground text-xs">Sex Mar 28</span>
                </Button>
                
                <Button
                  type="button"
                  size="sm"
                  variant={field.value && isSameDay(field.value, getNextWeekend()) ? "default" : "outline"}
                  className="flex items-center justify-between px-3 h-9"
                  onClick={() => field.onChange(getNextWeekend())}
                >
                  <div className="flex items-center gap-2">
                    <CalendarRange className="h-4 w-4 text-blue-500" />
                    <span>Next weekend</span>
                  </div>
                  <span className="text-muted-foreground text-xs">Sáb Mar 22</span>
                </Button>
              </div>
              
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "pl-3 text-left font-extrabold",
                        !field.value && "text-muted-foreground"
                      )}
                    >
                      {field.value ? (
                        formatDate(field.value)
                      ) : (
                        <span>Selecione uma data</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value || undefined}
                    onSelect={field.onChange}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="space-y-2">
          <Label>Etiquetas</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {selectedLabels.map(labelId => {
              const label = labels.find(l => l.id === labelId);
              return label ? (
                <Badge
                  key={label.id}
                  style={{ backgroundColor: label.color }}
                  className="text-white px-2 py-1 gap-1 cursor-pointer hover:brightness-90"
                  onClick={() => removeLabel(label.id)}
                >
                  {label.name}
                  <X className="h-3 w-3" />
                </Badge>
              ) : null;
            })}
          </div>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-sm"
                type="button"
                disabled={isLoadingLabels}
              >
                {isLoadingLabels ? "Carregando..." : "Adicionar etiqueta"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[200px]" align="start">
              <Command>
                <CommandInput placeholder="Buscar etiqueta..." />
                <CommandList>
                  <CommandEmpty>Nenhuma etiqueta encontrada</CommandEmpty>
                  <CommandGroup>
                    {isLoadingLabels ? (
                      <div className="py-2 px-2 text-sm text-center text-muted-foreground">Carregando etiquetas...</div>
                    ) : labels.length > 0 ? (
                      labels.map(label => (
                        <CommandItem
                          key={label.id}
                          onSelect={() => addLabel(label.id)}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: label.color }}
                          />
                          <span>{label.name}</span>
                        </CommandItem>
                      ))
                    ) : (
                      <div className="py-2 px-2 text-sm text-center text-muted-foreground">
                        Nenhuma etiqueta disponível. Crie etiquetas na área de Gerenciamento de Etiquetas.
                      </div>
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        
        <div className="flex items-center space-x-2">
          <FormField
            control={form.control}
            name="added_to_my_day"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Adicionar ao "Meu dia"</FormLabel>
                </div>
              </FormItem>
            )}
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <FormField
            control={form.control}
            name="is_personal"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Tarefa pessoal</FormLabel>
                  <FormDescription>
                    Tarefas pessoais não estão vinculadas a playbooks
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        </div>
        
        <div className="flex justify-between pt-4">
          <div className="flex gap-2">
            {onCancel && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={onCancel}
              >
                Cancelar
              </Button>
            )}
            {extraButtons}
          </div>
          <Button type="submit">
            {submitButtonLabel || (task ? "Salvar alterações" : "Criar tarefa")}
          </Button>
        </div>
      </form>
    </Form>
  );
} 